import { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../../config";
import { formatLeadDate } from "./formatLeadDate";
import { SHOW_GOOGLE_CALENDAR_UI } from "./leadsConstants";

export function useLeadsManagerState() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedLead, setExpandedLead] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteStatus, setDeleteStatus] = useState("");
  const [exportDate, setExportDate] = useState("7days");
  const [exportFormat, setExportFormat] = useState("csv");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [webhookMessage, setWebhookMessage] = useState("");
  const [webhookStatus, setWebhookStatus] = useState("");
  const [activeTab, setActiveTab] = useState("leads");
  const [rdStationApiToken, setRdStationApiToken] = useState("");
  const [rdStationConversionId, setRdStationConversionId] = useState("");
  const [rdStationMessage, setRdStationMessage] = useState("");
  const [rdStationStatus, setRdStationStatus] = useState("");
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [googleCalendarEmail, setGoogleCalendarEmail] = useState("");
  const [googleCalendarId, setGoogleCalendarId] = useState("primary");
  const [googleCalendarTimezone, setGoogleCalendarTimezone] = useState("America/Sao_Paulo");
  const [googleCalendarEnabled, setGoogleCalendarEnabled] = useState(true);
  const [googleCalendarCalendars, setGoogleCalendarCalendars] = useState([]);
  const [googleCalendarStartHour, setGoogleCalendarStartHour] = useState(9);
  const [googleCalendarEndHour, setGoogleCalendarEndHour] = useState(18);
  const [googleCalendarMessage, setGoogleCalendarMessage] = useState("");
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState("");
  const [leadNotificationEmails, setLeadNotificationEmails] = useState([]);
  const [newEmailInput, setNewEmailInput] = useState("");
  const [emailNotificationMessage, setEmailNotificationMessage] = useState("");
  const [emailNotificationStatus, setEmailNotificationStatus] = useState("");
  const [conversionMetaPixelId, setConversionMetaPixelId] = useState("");
  const [conversionMetaAccessToken, setConversionMetaAccessToken] = useState("");
  const [conversionMetaTestEventCode, setConversionMetaTestEventCode] = useState("");
  const [conversionGoogleCustomerId, setConversionGoogleCustomerId] = useState("");
  const [conversionGoogleActionId, setConversionGoogleActionId] = useState("");
  const [conversionGoogleSendTo, setConversionGoogleSendTo] = useState("");
  const [conversionGa4MeasurementId, setConversionGa4MeasurementId] = useState("");
  const [conversionGa4ApiSecret, setConversionGa4ApiSecret] = useState("");
  const [hasSavedGa4ApiSecret, setHasSavedGa4ApiSecret] = useState(false);
  const [conversionModeLead, setConversionModeLead] = useState("server_only");
  const [conversionModeAddToCart, setConversionModeAddToCart] = useState("hybrid");
  const [conversionModePurchase, setConversionModePurchase] = useState("server_only");
  const [conversionGatewayEnabled, setConversionGatewayEnabled] = useState(true);
  const [conversionStatusSummary, setConversionStatusSummary] = useState(null);
  const [conversionMessage, setConversionMessage] = useState("");
  const [conversionStatus, setConversionStatus] = useState("");
  const [metaFieldsTouched, setMetaFieldsTouched] = useState(false);
  const [hasSavedMetaAccessToken, setHasSavedMetaAccessToken] = useState(false);
  const loadGoogleCalendarConfig = () => {
    if (!selectedClient) return;
    axios
      .get(`${API_BASE}/api/google-calendar-config/${selectedClient}`)
      .then((response) => {
        const d = response.data || {};
        setGoogleCalendarConnected(Boolean(d.connected));
        setGoogleCalendarEmail(d.googleAccountEmail || "");
        setGoogleCalendarId(d.calendarId || "primary");
        setGoogleCalendarTimezone(d.timezone || "America/Sao_Paulo");
        setGoogleCalendarEnabled(d.enabled !== false);
        setGoogleCalendarStartHour(typeof d.startHour === "number" ? d.startHour : 9);
        setGoogleCalendarEndHour(typeof d.endHour === "number" ? d.endHour : 18);
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          setGoogleCalendarConnected(false);
          setGoogleCalendarEmail("");
          setGoogleCalendarId("primary");
          setGoogleCalendarTimezone("America/Sao_Paulo");
          setGoogleCalendarEnabled(true);
          setGoogleCalendarStartHour(9);
          setGoogleCalendarEndHour(18);
        } else {
          console.error("Erro ao buscar configuração do Google Agenda:", error);
          setGoogleCalendarConnected(false);
          setGoogleCalendarEmail("");
          setGoogleCalendarId("primary");
          setGoogleCalendarTimezone("America/Sao_Paulo");
          setGoogleCalendarEnabled(true);
          setGoogleCalendarStartHour(9);
          setGoogleCalendarEndHour(18);
        }
      });
  };

  const loadGoogleCalendars = () => {
    if (!selectedClient) return;
    axios
      .get(`${API_BASE}/api/google-calendar-calendars/${selectedClient}`)
      .then((response) => {
        setGoogleCalendarCalendars(Array.isArray(response.data?.calendars) ? response.data.calendars : []);
      })
      .catch((error) => {
        console.error("Erro ao listar calendários do Google:", error);
        setGoogleCalendarCalendars([]);
      });
  };


  // Buscar clientes ao carregar o componente
  useEffect(() => {
    axios
      .get(`${API_BASE}/api/clients`)
      .then((response) => setClients(response.data))
      .catch((error) => {
        console.error("Erro ao buscar clientes:", error);
        setError("Erro ao carregar a lista de clientes.");
      });
  }, []);

  // Buscar bots quando o cliente for selecionado
  useEffect(() => {
    if (!selectedClient) {
      setBots([]);
      setSelectedBot("");
      return;
    }
    setSelectedBot("");
    axios
      .get(`${API_BASE}/api/bots?clientId=${selectedClient}`)
      .then((response) => {
        setBots(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Erro ao buscar bots:", error);
        setBots([]);
      });
  }, [selectedClient]);

  // Buscar leads quando cliente/bot forem selecionados
  useEffect(() => {
    if (!selectedClient) return;
    setLoading(true);
    setError("");
    setLeads([]);

    const params = new URLSearchParams({ clientId: selectedClient });
    if (selectedBot) params.set("botId", selectedBot);

    axios
      .get(`${API_BASE}/api/leads?${params.toString()}`)
      .then((response) => {
        setLeads(response.data || []);
      })
      .catch((error) => {
        console.error("Erro ao buscar leads:", error);
        setError("Erro ao carregar os leads: " + (error.response?.data?.error || error.message));
      })
      .finally(() => setLoading(false));
  }, [selectedClient, selectedBot]);

  // Buscar configuração de webhook somente quando a aba estiver ativa
  useEffect(() => {
    if (!selectedClient || activeTab !== "webhook") return;
    axios
      .get(`${API_BASE}/api/webhook-config/${selectedClient}`)
      .then((response) => {
        if (response.data && response.data.url) {
          setWebhookUrl(response.data.url);
          setWebhookSecret(response.data.secret || "");
        } else {
          setWebhookUrl("");
          setWebhookSecret("");
        }
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          setWebhookUrl("");
          setWebhookSecret("");
        } else {
          console.error("Erro ao buscar configuração de webhook:", error);
          setWebhookUrl("");
          setWebhookSecret("");
        }
      });
  }, [selectedClient, activeTab]);

  // Buscar configuração de conversão somente quando a aba estiver ativa
  useEffect(() => {
    if (selectedClient && activeTab === "conversion") {
      setConversionMetaPixelId("");
      setConversionMetaAccessToken("");
      setConversionMetaTestEventCode("");
      setMetaFieldsTouched(false);
      setHasSavedMetaAccessToken(false);
      setConversionGa4MeasurementId("");
      setConversionGa4ApiSecret("");
      setHasSavedGa4ApiSecret(false);
      setConversionModeLead("server_only");
      setConversionModeAddToCart("hybrid");
      setConversionModePurchase("server_only");
      setConversionGatewayEnabled(true);
      setConversionStatusSummary(null);
      axios
        .get(`${API_BASE}/api/conversion-config/${selectedClient}`)
        .then((response) => {
          const d = response.data || {};
          setHasSavedMetaAccessToken(Boolean(d.meta?.hasAccessToken));
          setConversionMetaPixelId(d.meta?.pixelId || "");
          setConversionMetaTestEventCode(d.meta?.testEventCode || "");
          setConversionGa4MeasurementId(d.ga4?.measurementId || "");
          setHasSavedGa4ApiSecret(Boolean(d.ga4?.hasApiSecret));
          setConversionGoogleCustomerId(d.google?.customerId || "");
          setConversionGoogleActionId(d.google?.conversionActionId || "");
          setConversionGoogleSendTo(d.googleConversionSendTo || "");
          setConversionModeLead(d.eventModes?.Lead || "server_only");
          setConversionModeAddToCart(d.eventModes?.AddToCart || "hybrid");
          setConversionModePurchase(d.eventModes?.Purchase || "server_only");
          setConversionGatewayEnabled(d.conversionGatewayV2 !== false);
        })
        .catch(() => {
          setHasSavedMetaAccessToken(false);
          setHasSavedGa4ApiSecret(false);
          setConversionMetaPixelId("");
          setConversionMetaTestEventCode("");
          setConversionGa4MeasurementId("");
          setConversionGoogleCustomerId("");
          setConversionGoogleActionId("");
          setConversionGoogleSendTo("");
          setConversionModeLead("server_only");
          setConversionModeAddToCart("hybrid");
          setConversionModePurchase("server_only");
          setConversionGatewayEnabled(true);
        });

      axios
        .get(`${API_BASE}/api/conversion-events/status/${selectedClient}`)
        .then((response) => {
          setConversionStatusSummary(response.data || null);
        })
        .catch(() => setConversionStatusSummary(null));
    }
  }, [selectedClient, activeTab]);

  // Buscar configuração do RD Station somente quando a aba estiver ativa
  useEffect(() => {
    if (selectedClient && activeTab === "rdstation") {
      axios
        .get(`${API_BASE}/api/rdstation-config/${selectedClient}`)
        .then((response) => {
          if (response.data && response.data.apiToken) {
            setRdStationApiToken(response.data.apiToken);
            setRdStationConversionId(response.data.conversionIdentifier || "neurobot-lead");
          } else {
            setRdStationApiToken("");
            setRdStationConversionId("neurobot-lead");
          }
        })
        .catch((error) => {
          if (error.response?.status === 404) {
            setRdStationApiToken("");
            setRdStationConversionId("neurobot-lead");
          } else {
            console.error("Erro ao buscar configuração do RD Station:", error);
            setRdStationApiToken("");
            setRdStationConversionId("neurobot-lead");
          }
        });
    }
  }, [selectedClient, activeTab]);

  useEffect(() => {
    if (SHOW_GOOGLE_CALENDAR_UI && selectedClient && activeTab === "google_calendar") {
      loadGoogleCalendarConfig();
      loadGoogleCalendars();
    }
    if (!selectedClient) {
      setGoogleCalendarConnected(false);
      setGoogleCalendarEmail("");
      setGoogleCalendarId("primary");
      setGoogleCalendarTimezone("America/Sao_Paulo");
      setGoogleCalendarEnabled(true);
      setGoogleCalendarStartHour(9);
      setGoogleCalendarEndHour(18);
      setGoogleCalendarCalendars([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- efeito só quando cliente/aba mudam; funções de load são estáveis no uso
  }, [selectedClient, activeTab]);

  // Buscar emails de notificação somente quando a aba estiver ativa
  useEffect(() => {
    if (selectedClient && activeTab === "email") {
      axios
        .get(`${API_BASE}/api/lead-notification-emails/${selectedClient}`)
        .then((response) => {
          setLeadNotificationEmails(response.data?.emails || []);
        })
        .catch((error) => {
          console.error("Erro ao buscar emails de notificação:", error);
          setLeadNotificationEmails([]);
        });
    } else if (!selectedClient) {
      setLeadNotificationEmails([]);
    }
  }, [selectedClient, activeTab]);

  // Alternar exibição expandida de um lead
  const toggleLeadExpansion = (leadId) => {
    if (expandedLead === leadId) {
      setExpandedLead(null);
    } else {
      setExpandedLead(leadId);
    }
  };

  // Função para lidar com a seleção de leads para exclusão
  const handleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Função para excluir leads selecionados
  const handleDeleteLeads = async () => {
    if (selectedLeads.length === 0) {
      setDeleteMessage("Por favor, selecione pelo menos um lead para excluir.");
      setDeleteStatus("error");
      return;
    }

    const confirmDelete = window.confirm(`Tem certeza que deseja excluir ${selectedLeads.length} lead(s)? Esta ação não pode ser desfeita.`);
    if (!confirmDelete) return;

    try {
      setLoading(true);
      const response = await axios.delete(`${API_BASE}/api/leads`, {
        data: { leadIds: selectedLeads }
      });

      // Remover os leads excluídos da lista local
      setLeads(currentLeads => currentLeads.filter(lead => !selectedLeads.includes(lead._id)));
      setSelectedLeads([]);
      setDeleteMessage(`${response.data.deletedCount} lead(s) excluído(s) com sucesso.`);
      setDeleteStatus("success");
    } catch (error) {
      console.error("Erro ao excluir leads:", error);
      setDeleteMessage("Erro ao excluir leads. Tente novamente.");
      setDeleteStatus("error");
    } finally {
      setLoading(false);
    }
  };  


  // Salvar configuração de webhook
  const saveWebhookConfig = () => {
    setWebhookMessage("");
    setWebhookStatus("");
    
    if (!selectedClient) {
      setWebhookMessage("Por favor, selecione um cliente.");
      setWebhookStatus("error");
      return;
    }
    
    if (!webhookUrl) {
      setWebhookMessage("A URL do webhook é obrigatória.");
      setWebhookStatus("error");
      return;
    }
    
    axios
      .post(`${API_BASE}/api/webhook-config`, {
        clientId: selectedClient,
        url: webhookUrl,
        secret: webhookSecret
      })
      .then((response) => {
        setWebhookMessage("Configuração de webhook salva com sucesso!");
        setWebhookStatus("success");
      })
      .catch((error) => {
        console.error("Erro ao salvar configuração de webhook:", error);
        setWebhookMessage("Erro ao salvar configuração de webhook.");
        setWebhookStatus("error");
      });
  };

  // Exportar leads
  const handleExportLeads = () => {
    if (!selectedClient) {
      setError("Por favor, selecione um cliente para exportar os leads.");
      return;
    }

    let url = `${API_BASE}/api/export-leads?clientId=${selectedClient}&format=${exportFormat}`;
    if (selectedBot) {
      url += `&botId=${encodeURIComponent(selectedBot)}`;
    }
    
    if (exportDate === "7days") {
      url += "&range=7days";
    } else if (exportDate !== "all") {
      url += `&date=${exportDate}`;
    }

    // Redirecionar para o download do arquivo
    window.location.href = url;
  };

  // Testar webhook
  const testWebhook = () => {
    if (!selectedClient || !webhookUrl) {
      setWebhookMessage("Por favor, configure a URL do webhook primeiro.");
      setWebhookStatus("error");
      return;
    }
    
    axios
      .post(`${API_BASE}/api/test-webhook`, {
        clientId: selectedClient
      })
      .then((response) => {
        setWebhookMessage("Teste de webhook enviado com sucesso!");
        setWebhookStatus("success");
      })
      .catch((error) => {
        console.error("Erro ao testar webhook:", error);
        setWebhookMessage(`Erro ao testar webhook: ${error.response?.data?.error || error.message}`);
        setWebhookStatus("error");
      });
  };

  // Salvar configuração do RD Station
  const saveRdStationConfig = () => {
    setRdStationMessage("");
    setRdStationStatus("");
    
    if (!selectedClient) {
      setRdStationMessage("Por favor, selecione um cliente.");
      setRdStationStatus("error");
      return;
    }
    
    if (!rdStationApiToken) {
      setRdStationMessage("O token da API do RD Station é obrigatório.");
      setRdStationStatus("error");
      return;
    }
    
    axios
      .post(`${API_BASE}/api/rdstation-config`, {
        clientId: selectedClient,
        apiToken: rdStationApiToken,
        conversionIdentifier: rdStationConversionId || "neurobot-lead"
      })
      .then((response) => {
        setRdStationMessage("Configuração do RD Station salva com sucesso!");
        setRdStationStatus("success");
      })
      .catch((error) => {
        console.error("Erro ao salvar configuração do RD Station:", error);
        setRdStationMessage("Erro ao salvar configuração do RD Station.");
        setRdStationStatus("error");
      });
  };

  // Testar conexão com RD Station
  const testRdStation = () => {
    if (!selectedClient || !rdStationApiToken) {
      setRdStationMessage("Por favor, configure o token da API do RD Station primeiro.");
      setRdStationStatus("error");
      return;
    }
    
    axios
      .post(`${API_BASE}/api/test-rdstation`, {
        clientId: selectedClient
      })
      .then((response) => {
        setRdStationMessage("Conexão com RD Station testada com sucesso!");
        setRdStationStatus("success");
      })
      .catch((error) => {
        console.error("Erro ao testar conexão com RD Station:", error);
        setRdStationMessage(`Erro ao testar conexão com RD Station: ${error.response?.data?.error || error.message}`);
        setRdStationStatus("error");
      });
  };

  // Remover integração RD Station
  const removeRdStationConfig = () => {
    if (!selectedClient) return;
    if (!window.confirm("Remover a integração com RD Station? Os leads deixarão de ser enviados para essa plataforma.")) return;
    setRdStationMessage("");
    axios
      .delete(`${API_BASE}/api/rdstation-config`, { data: { clientId: selectedClient } })
      .then(() => {
        setRdStationApiToken("");
        setRdStationConversionId("neurobot-lead");
        setRdStationMessage("Integração RD Station removida.");
        setRdStationStatus("success");
      })
      .catch((err) => {
        setRdStationMessage(err.response?.data?.error || "Erro ao remover integração.");
        setRdStationStatus("error");
      });
  };

  const connectGoogleCalendar = () => {
    setGoogleCalendarMessage("");
    setGoogleCalendarStatus("");
    if (!selectedClient) {
      setGoogleCalendarMessage("Por favor, selecione um cliente.");
      setGoogleCalendarStatus("error");
      return;
    }
    axios
      .post(`${API_BASE}/api/google-calendar/oauth-url`, { clientId: selectedClient })
      .then((response) => {
        const authUrl = response.data?.authUrl;
        if (!authUrl) {
          setGoogleCalendarMessage("Não foi possível iniciar autenticação Google.");
          setGoogleCalendarStatus("error");
          return;
        }
        const popup = window.open(authUrl, "googleCalendarOAuth", "width=520,height=720");
        if (!popup) {
          setGoogleCalendarMessage("O navegador bloqueou o popup. Libere popups e tente novamente.");
          setGoogleCalendarStatus("error");
          return;
        }
        const poll = window.setInterval(() => {
          if (popup.closed) {
            window.clearInterval(poll);
            setGoogleCalendarMessage("Autenticação concluída. Atualizando status...");
            setGoogleCalendarStatus("success");
            loadGoogleCalendarConfig();
            loadGoogleCalendars();
          }
        }, 1000);
      })
      .catch((error) => {
        console.error("Erro ao iniciar OAuth do Google Agenda:", error);
        setGoogleCalendarMessage(error.response?.data?.error || "Erro ao iniciar conexão com Google Agenda.");
        setGoogleCalendarStatus("error");
      });
  };

  const saveGoogleCalendarConfig = () => {
    setGoogleCalendarMessage("");
    setGoogleCalendarStatus("");
    if (!selectedClient) {
      setGoogleCalendarMessage("Por favor, selecione um cliente.");
      setGoogleCalendarStatus("error");
      return;
    }
    axios
      .post(`${API_BASE}/api/google-calendar-config`, {
        clientId: selectedClient,
        calendarId: googleCalendarId,
        timezone: googleCalendarTimezone,
        enabled: googleCalendarEnabled,
        startHour: googleCalendarStartHour,
        endHour: googleCalendarEndHour
      })
      .then(() => {
        setGoogleCalendarMessage("Configuração do Google Agenda salva com sucesso!");
        setGoogleCalendarStatus("success");
      })
      .catch((error) => {
        console.error("Erro ao salvar configuração Google Agenda:", error);
        setGoogleCalendarMessage(error.response?.data?.error || "Erro ao salvar configuração Google Agenda.");
        setGoogleCalendarStatus("error");
      });
  };

  const testGoogleCalendar = () => {
    setGoogleCalendarMessage("");
    setGoogleCalendarStatus("");
    if (!selectedClient) {
      setGoogleCalendarMessage("Por favor, selecione um cliente.");
      setGoogleCalendarStatus("error");
      return;
    }
    axios
      .post(`${API_BASE}/api/test-google-calendar`, { clientId: selectedClient })
      .then((response) => {
        setGoogleCalendarMessage(response.data?.message || "Conexão testada com sucesso.");
        setGoogleCalendarStatus("success");
        loadGoogleCalendars();
      })
      .catch((error) => {
        console.error("Erro ao testar Google Agenda:", error);
        setGoogleCalendarMessage(error.response?.data?.error || "Erro ao testar conexão com Google Agenda.");
        setGoogleCalendarStatus("error");
      });
  };

  const createGoogleCalendarTestEvent = () => {
    setGoogleCalendarMessage("");
    setGoogleCalendarStatus("");
    if (!selectedClient) {
      setGoogleCalendarMessage("Por favor, selecione um cliente.");
      setGoogleCalendarStatus("error");
      return;
    }
    const start = new Date(Date.now() + 60 * 60 * 1000);
    const end = new Date(start.getTime() + 30 * 60 * 1000);

    axios
      .post(`${API_BASE}/api/google-calendar/create-event`, {
        clientId: selectedClient,
        title: "Teste de agendamento - Tilti",
        description: "Evento de teste criado pelo painel de integrações.",
        startDateTime: start.toISOString(),
        endDateTime: end.toISOString(),
        timezone: googleCalendarTimezone
      })
      .then((response) => {
        const link = response.data?.event?.htmlLink;
        setGoogleCalendarMessage(
          link
            ? `Evento criado com sucesso. Link: ${link}`
            : "Evento de teste criado com sucesso."
        );
        setGoogleCalendarStatus("success");
      })
      .catch((error) => {
        console.error("Erro ao criar evento de teste no Google Agenda:", error);
        setGoogleCalendarMessage(error.response?.data?.error || "Erro ao criar evento de teste.");
        setGoogleCalendarStatus("error");
      });
  };

  const removeGoogleCalendarConfig = () => {
    if (!selectedClient) return;
    if (!window.confirm("Remover a integração com Google Agenda?")) return;
    setGoogleCalendarMessage("");
    axios
      .delete(`${API_BASE}/api/google-calendar-config`, { data: { clientId: selectedClient } })
      .then(() => {
        setGoogleCalendarConnected(false);
        setGoogleCalendarEmail("");
        setGoogleCalendarId("primary");
        setGoogleCalendarStartHour(9);
        setGoogleCalendarEndHour(18);
        setGoogleCalendarCalendars([]);
        setGoogleCalendarMessage("Integração Google Agenda removida.");
        setGoogleCalendarStatus("success");
      })
      .catch((err) => {
        setGoogleCalendarMessage(err.response?.data?.error || "Erro ao remover integração Google Agenda.");
        setGoogleCalendarStatus("error");
      });
  };

  // Salvar emails de notificação de leads
  const saveLeadNotificationEmails = () => {
    setEmailNotificationMessage("");
    setEmailNotificationStatus("");
    if (!selectedClient) {
      setEmailNotificationMessage("Por favor, selecione um cliente.");
      setEmailNotificationStatus("error");
      return;
    }
    axios
      .put(`${API_BASE}/api/lead-notification-emails/${selectedClient}`, {
        emails: leadNotificationEmails
      })
      .then((response) => {
        setLeadNotificationEmails(response.data?.emails || []);
        setEmailNotificationMessage("Emails de notificação salvos com sucesso!");
        setEmailNotificationStatus("success");
      })
      .catch((error) => {
        console.error("Erro ao salvar emails de notificação:", error);
        setEmailNotificationMessage(error.response?.data?.error || "Erro ao salvar.");
        setEmailNotificationStatus("error");
      });
  };

  const addEmailToNotificationList = () => {
    const email = newEmailInput.trim().toLowerCase();
    if (!email) return;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email)) {
      setEmailNotificationMessage("Digite um email válido.");
      setEmailNotificationStatus("error");
      return;
    }
    if (leadNotificationEmails.includes(email)) {
      setEmailNotificationMessage("Este email já está na lista.");
      setEmailNotificationStatus("error");
      return;
    }
    setLeadNotificationEmails(prev => [...prev, email]);
    setNewEmailInput("");
    setEmailNotificationMessage("");
  };

  const removeEmailFromNotificationList = (emailToRemove) => {
    setLeadNotificationEmails(prev => prev.filter(e => e !== emailToRemove));
  };

  const saveConversionConfig = () => {
    setConversionMessage("");
    setConversionStatus("");
    if (!selectedClient) {
      setConversionMessage("Selecione um cliente.");
      setConversionStatus("error");
      return;
    }
    const payload = {
      ga4: {
        measurementId: conversionGa4MeasurementId.trim() || null,
        apiSecret: conversionGa4ApiSecret.trim() || null
      },
      google: {
        customerId: conversionGoogleCustomerId.trim() || null,
        conversionActionId: conversionGoogleActionId.trim() || null
      },
      googleConversionSendTo: conversionGoogleSendTo.trim() || null,
      eventModes: {
        Lead: conversionModeLead,
        AddToCart: conversionModeAddToCart,
        Purchase: conversionModePurchase
      },
      conversionGatewayV2: conversionGatewayEnabled
    };
    if (metaFieldsTouched) {
      payload.meta = {
        pixelId: conversionMetaPixelId.trim() || null,
        accessToken: conversionMetaAccessToken.trim() || null,
        testEventCode: conversionMetaTestEventCode.trim() || null
      };
    }

    axios
      .put(`${API_BASE}/api/conversion-config/${selectedClient}`, payload)
      .then(() => {
        setConversionMessage("Configuração de conversão salva.");
        setConversionStatus("success");
        if (metaFieldsTouched) {
          setHasSavedMetaAccessToken(Boolean(conversionMetaAccessToken.trim()));
          setMetaFieldsTouched(false);
          setConversionMetaAccessToken("");
        }
        setHasSavedGa4ApiSecret(Boolean(conversionGa4ApiSecret.trim()) || hasSavedGa4ApiSecret);
        if (conversionGa4ApiSecret.trim()) setConversionGa4ApiSecret("");
        axios
          .get(`${API_BASE}/api/conversion-events/status/${selectedClient}`)
          .then((response) => setConversionStatusSummary(response.data || null))
          .catch(() => {});
      })
      .catch((err) => {
        setConversionMessage(err.response?.data?.error || "Erro ao salvar.");
        setConversionStatus("error");
      });
  };

  // Remover integração Webhook
  const removeWebhookConfig = () => {
    if (!selectedClient) return;
    if (!window.confirm("Remover a integração por webhook? Os leads deixarão de ser enviados para a URL configurada.")) return;
    setWebhookMessage("");
    axios
      .delete(`${API_BASE}/api/webhook-config`, { data: { clientId: selectedClient } })
      .then(() => {
        setWebhookUrl("");
        setWebhookSecret("");
        setWebhookMessage("Integração por webhook removida.");
        setWebhookStatus("success");
      })
      .catch((err) => {
        setWebhookMessage(err.response?.data?.error || "Erro ao remover integração.");
        setWebhookStatus("error");
      });
  };


  const processPendingConversations = () => {
    if (!selectedClient) {
      setError("Por favor, selecione um cliente primeiro.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    axios
      .post(`${API_BASE}/api/process-pending-conversations`, {
        clientId: selectedClient,
        ...(selectedBot ? { botId: selectedBot } : {})
      })
      .then((response) => {
        console.log("Processamento manual concluído:", response.data);
        setLoading(false);
        
        // Exibir mensagem de sucesso
        alert(`Processamento concluído: ${response.data.message}`);
        
        // Recarregar os leads após o processamento
        const params = new URLSearchParams({ clientId: selectedClient });
        if (selectedBot) params.set("botId", selectedBot);
        axios
          .get(`${API_BASE}/api/leads?${params.toString()}`)
          .then((response) => {
            setLeads(response.data || []);
          })
          .catch((error) => {
            console.error("Erro ao recarregar leads:", error);
          });
      })
      .catch((error) => {
        console.error("Erro ao processar conversas pendentes:", error);
        setError("Erro ao processar conversas pendentes: " + (error.response?.data?.error || error.message));
        setLoading(false);
      });
  };

  const botNameById = bots.reduce((acc, bot) => {
    if (bot && bot.id) acc[bot.id] = bot.name || bot.id;
    return acc;
  }, {});

  return {
    clients,
    setClients,
    selectedClient,
    setSelectedClient,
    bots,
    setBots,
    selectedBot,
    setSelectedBot,
    leads,
    setLeads,
    loading,
    setLoading,
    error,
    setError,
    expandedLead,
    setExpandedLead,
    selectedLeads,
    setSelectedLeads,
    deleteMessage,
    setDeleteMessage,
    deleteStatus,
    setDeleteStatus,
    exportDate,
    setExportDate,
    exportFormat,
    setExportFormat,
    webhookUrl,
    setWebhookUrl,
    webhookSecret,
    setWebhookSecret,
    webhookMessage,
    setWebhookMessage,
    webhookStatus,
    setWebhookStatus,
    activeTab,
    setActiveTab,
    rdStationApiToken,
    setRdStationApiToken,
    rdStationConversionId,
    setRdStationConversionId,
    rdStationMessage,
    setRdStationMessage,
    rdStationStatus,
    setRdStationStatus,
    googleCalendarConnected,
    setGoogleCalendarConnected,
    googleCalendarEmail,
    setGoogleCalendarEmail,
    googleCalendarId,
    setGoogleCalendarId,
    googleCalendarTimezone,
    setGoogleCalendarTimezone,
    googleCalendarEnabled,
    setGoogleCalendarEnabled,
    googleCalendarCalendars,
    setGoogleCalendarCalendars,
    googleCalendarStartHour,
    setGoogleCalendarStartHour,
    googleCalendarEndHour,
    setGoogleCalendarEndHour,
    googleCalendarMessage,
    setGoogleCalendarMessage,
    googleCalendarStatus,
    setGoogleCalendarStatus,
    leadNotificationEmails,
    setLeadNotificationEmails,
    newEmailInput,
    setNewEmailInput,
    emailNotificationMessage,
    setEmailNotificationMessage,
    emailNotificationStatus,
    setEmailNotificationStatus,
    conversionMetaPixelId,
    setConversionMetaPixelId,
    conversionMetaAccessToken,
    setConversionMetaAccessToken,
    conversionMetaTestEventCode,
    setConversionMetaTestEventCode,
    conversionGoogleCustomerId,
    setConversionGoogleCustomerId,
    conversionGoogleActionId,
    setConversionGoogleActionId,
    conversionGoogleSendTo,
    setConversionGoogleSendTo,
    conversionGa4MeasurementId,
    setConversionGa4MeasurementId,
    conversionGa4ApiSecret,
    setConversionGa4ApiSecret,
    hasSavedGa4ApiSecret,
    setHasSavedGa4ApiSecret,
    conversionModeLead,
    setConversionModeLead,
    conversionModeAddToCart,
    setConversionModeAddToCart,
    conversionModePurchase,
    setConversionModePurchase,
    conversionGatewayEnabled,
    setConversionGatewayEnabled,
    conversionStatusSummary,
    setConversionStatusSummary,
    conversionMessage,
    setConversionMessage,
    conversionStatus,
    setConversionStatus,
    metaFieldsTouched,
    setMetaFieldsTouched,
    hasSavedMetaAccessToken,
    setHasSavedMetaAccessToken,
    loadGoogleCalendarConfig,
    loadGoogleCalendars,
    toggleLeadExpansion,
    handleLeadSelection,
    handleDeleteLeads,
    saveWebhookConfig,
    handleExportLeads,
    testWebhook,
    saveRdStationConfig,
    testRdStation,
    removeRdStationConfig,
    connectGoogleCalendar,
    saveGoogleCalendarConfig,
    testGoogleCalendar,
    createGoogleCalendarTestEvent,
    removeGoogleCalendarConfig,
    saveLeadNotificationEmails,
    addEmailToNotificationList,
    removeEmailFromNotificationList,
    saveConversionConfig,
    removeWebhookConfig,
    processPendingConversations,
    botNameById,
    formatDate: formatLeadDate,
    SHOW_GOOGLE_CALENDAR_UI,
  };
}
